import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import css from './form-field.module.css';

interface FormFieldProps {
  label?: string;
  labelFor?: string;
  required?: boolean;
  className?: string;
  error?: string | string[];
  children: ReactNode;
  message?: string;
  inline?: boolean;
  isNoteForm?: boolean;
  horizontal?: boolean;
  column?: boolean;
  creatable?: boolean;
}

export default function FormField({
  label,
  labelFor,
  required,
  className,
  error,
  children,
  message,
  inline = false,
  isNoteForm = false,
  horizontal = false,
  column,
  creatable,
}: FormFieldProps) {
  const { t } = useTranslation();

  const labelClasses = ''
    .concat('form-field-label', ' ')
    .concat(css['label'], ' ')
    .concat(
      (Array.isArray(error) && error.filter(Boolean).length) ||
        (!Array.isArray(error) && error)
        ? css['error']
        : ''
    );

  const errorClasses = ''.concat('form-field-error', ' ').concat(css['error']);

  const inputClasses = ''.concat('form-field-input', ' ').concat(css['input']);

  const fieldClasses = ''
    .concat('form-field', ' ')
    .concat(css['form-field'], ' ')
    .concat(column ? css['column'] : '', ' ')
    .concat(inline && !isNoteForm ? css['inline'] : '', ' ')
    .concat(horizontal ? css['horizontal'] : '', ' ')
    .concat(className ?? '');

  const requiredMark = required ? (
    <span className={css['required']}>*</span>
  ) : null;

  const labelElement = inline ? null : (
    <div className={labelClasses}>
      <label htmlFor={labelFor} data-required={required}>
        {label}
        {requiredMark}
      </label>
    </div>
  );

  let errorElement: ReactElement | ReactElement[];

  if (Array.isArray(error)) {
    errorElement = error.length
      ? error.filter(Boolean).map((e, index) => (
          <div key={index} className={errorClasses}>
            {t(e)}
          </div>
        ))
      : null;
  } else {
    errorElement = error ? (
      <div className={errorClasses}>{t(error)}</div>
    ) : null;
  }

  let messageElement = message ? (
    <div className={css['success']}>{t(message)}</div>
  ) : null;

  return (
    <div className={fieldClasses}>
      {label ? labelElement : null}
      <div className={inputClasses}>
        {children}
        {errorElement}
        {messageElement}
      </div>
    </div>
  );
}
